'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import LayoutDefault from '../../layouts/LayoutDefault';
import TrackDeliveryStatusDetail from './trackDeliveryStatusDetail';
import { FilterIcon, SearchIcon, ArrowDownIcon, SwitchModeIcon, LoadingSpinnerIcon } from "../../utils/icons";
import { useIdPath } from '@/contexts/IdPathProvider';
import { assetLoanService } from '@/services/assetLoan.service';
import { LoanRequest, LoanFilters } from '@/types/asset-loan.types';

// Delivery status types - map from loan status
type DeliveryStatus = 'waiting-delivery' | 'borrowing' | 'due-date' | 'overdue' | 'returned';

interface DeliveryRequest {
    id: string;
    loanId: number;
    borrowerName: string;
    unit: string;
    status: DeliveryStatus;
    dueDate: string;
    loanStatus: string;
    overdueDays?: number;
}

// Map loan status to delivery status
const mapLoanStatusToDelivery = (loan: LoanRequest): DeliveryStatus => {
    switch (loan.status) {
        case 'approved':
            return 'waiting-delivery';
        case 'borrowed': {
            if (loan.expected_return_date) {
                const returnDate = new Date(loan.expected_return_date);
                const today = new Date();
                if (today > returnDate) {
                    return 'overdue';
                }
                const diffDays = Math.ceil((returnDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                if (diffDays <= 1) {
                    return 'due-date';
                }
            }
            return 'borrowing';
        }
        case 'overdue':
            return 'overdue';
        case 'partially_returned':
            return 'due-date';
        case 'returned':
            return 'returned';
        case 'cancelled':
        case 'rejected':
            return 'returned';
        default:
            return 'waiting-delivery';
    }
};

interface TrackDeliveryStatusAdminProps {
    onClose?: () => void;
    isSidebarOpen: boolean;
    setMode?: (mode: 'admin' | 'partner') => void;
    isEmbedded?: boolean;
}

interface FilterOptions {
    status: string;
    handover_type?: string;
    date_from?: string;
    date_to?: string;
}

export default function TrackDeliveryStatusAdmin({ onClose, isSidebarOpen, setMode, isEmbedded }: TrackDeliveryStatusAdminProps) {
    const { idPath } = useIdPath();
    const [deliveryRequests, setDeliveryRequests] = useState<DeliveryRequest[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
    const [searchTerm, setSearchTerm] = useState('');
    const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
    const [filters, setFilters] = useState<FilterOptions>({
        status: ''
    });
    const [showDeliveryDetailPage, setShowDeliveryDetailPage] = useState(false);
    const [selectedDeliveryId, setSelectedDeliveryId] = useState<string>('');
    const [selectedDeliveryStatus, setSelectedDeliveryStatus] = useState<'new-incoming' | 'update' | 'returned' | 'not-returned'>('new-incoming');
    const filterButtonRef = useRef<HTMLButtonElement>(null);
    const filterPopupRef = useRef<HTMLDivElement>(null);

    // Fetch approved/borrowed loan requests from API
    const fetchDeliveryRecords = useCallback(async () => {
        if (!idPath) return;

        setLoading(true);
        setError(null);
        try {
            const filterParams: LoanFilters = {
                page: pagination.page,
                limit: pagination.limit,
                search: searchTerm || undefined,
                status: filters.status || undefined,
            };

            const response = await assetLoanService.getLoanRequests(idPath, filterParams);
            // response is PaginatedResponse<LoanRequest> = { data: [...], pagination: {...} }
            const rawData = response?.data || [];
            const loanRecords = Array.isArray(rawData) ? rawData : [];

            // Filter only post-approval statuses for handover tracking
            const trackableStatuses = ['approved', 'borrowed', 'overdue', 'partially_returned', 'returned'];
            const filteredRecords = filters.status
                ? loanRecords  // If specific filter is set, show all matching
                : loanRecords.filter((loan: LoanRequest) => trackableStatuses.includes(loan.status));

            // Map loan records to delivery requests
            const mappedData: DeliveryRequest[] = filteredRecords.map((loan: LoanRequest) => {
                let overdueDays = 0;
                const status = mapLoanStatusToDelivery(loan);
                if (status === 'overdue' && loan.expected_return_date) {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const returnDate = new Date(loan.expected_return_date);
                    returnDate.setHours(0, 0, 0, 0);
                    overdueDays = Math.floor((today.getTime() - returnDate.getTime()) / (1000 * 60 * 60 * 24));
                }
                
                return {
                    id: loan.loan_code,
                    loanId: loan.loan_id,
                    borrowerName: loan.borrower_name,
                    unit: loan.borrower_department || '—',
                    status: status,
                    dueDate: loan.expected_return_date
                        ? new Date(loan.expected_return_date).toLocaleDateString('vi-VN')
                        : '—',
                    loanStatus: loan.status,
                    overdueDays: overdueDays > 0 ? overdueDays : undefined,
                };
            });

            setDeliveryRequests(mappedData);
            const paginationData = response?.pagination;
            setPagination(prev => ({
                ...prev,
                total: paginationData?.total || 0,
                totalPages: paginationData?.totalPages || 0
            }));
        } catch (err: any) {
            console.error('Error fetching delivery records:', err);
            setError(err.message || 'Không thể tải danh sách bàn giao');
            setDeliveryRequests([]);
        } finally {
            setLoading(false);
        }
    }, [idPath, pagination.page, pagination.limit, searchTerm, filters.status]);

    // Initial fetch and refetch when filters change
    useEffect(() => {
        if (idPath) {
            fetchDeliveryRecords();
        }
    }, [idPath, fetchDeliveryRecords]);

    // Close filter popup when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                filterPopupRef.current &&
                !filterPopupRef.current.contains(event.target as Node) &&
                filterButtonRef.current &&
                !filterButtonRef.current.contains(event.target as Node)
            ) {
                setShowAdvancedFilter(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleFilterChange = (field: keyof FilterOptions, value: string) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const clearFilters = () => {
        setFilters({
            status: ''
        });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const toggleAdvancedFilter = () => {
        setShowAdvancedFilter(prev => !prev);
    };

    // Handle search
    const handleSearch = () => {
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchDeliveryRecords();
    };

    // Handle page change
    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({ ...prev, page: newPage }));
        }
    };

    const getStatusColor = (status: DeliveryStatus): string => {
        switch (status) {
            case 'waiting-delivery':
                return '#FFFFFF';
            case 'borrowing':
                return '#FFFFFF';
            case 'due-date':
                return '#FFFFFF';
            case 'overdue':
                return '#FFFFFF';
            case 'returned':
                return '#656565';
            default:
                return '#FFFFFF';
        }
    };

    const getStatusText = (status: DeliveryStatus, overdueDays?: number): string => {
        switch (status) {
            case 'waiting-delivery':
                return 'Chờ bàn giao';
            case 'borrowing':
                return 'Đang mượn';
            case 'due-date':
                return 'Đến hạn trả';
            case 'overdue':
                return overdueDays ? `Quá hạn - ${String(overdueDays).padStart(2, '0')} ngày` : 'Quá hạn';
            case 'returned':
                return 'Đã trả';
            default:
                return '';
        }
    };

    const getStatusBgColor = (status: DeliveryStatus): string => {
        switch (status) {
            case 'waiting-delivery':
                return 'rgba(229, 206, 0, 0.5)';
            case 'borrowing':
                return 'rgba(0, 164, 23, 0.1)';
            case 'due-date':
                return '#FCE6E6';
            case 'overdue':
                return '#9F0000';
            case 'returned':
                return '#E0E0E0';
            default:
                return '#FFFFFF';
        }
    };

    const getStatusTextColor = (status: DeliveryStatus): string => {
        switch (status) {
            case 'overdue':
                return '#FFFFFF';
            default:
                return '#000000';
        }
    };

    const getActionText = (status: DeliveryStatus): { delivery: string; return: string } => {
        switch (status) {
            case 'waiting-delivery':
                return { delivery: 'Khởi tạo', return: '' };
            case 'borrowing':
            case 'due-date':
            case 'overdue':
                return { delivery: 'Biên bản bàn giao', return: 'Lập phiếu trả' };
            case 'returned':
                return { delivery: 'Xem', return: '' };
            default:
                return { delivery: 'Xem', return: '' };
        }
    };

    const getActionButtonStyle = (action: string): string => {
        if (action === 'Xem' || action === 'Biên bản bàn giao') {
            return 'bg-transparent text-[#00A417] hover:bg-gray-50';
        }
        if (action === 'Khởi tạo' || action === 'Lập phiếu trả') {
            return 'bg-[#ECECEC] hover:bg-gray-300 text-black';
        }
        return 'bg-[#ECECEC] hover:bg-gray-300 text-black';
    };

    const handleDeliveryAction = (requestId: string, status: DeliveryStatus, action: string) => {
        setSelectedDeliveryId(requestId);
        setSelectedDeliveryStatus('new-incoming');
        setShowDeliveryDetailPage(true);
    };

    return (
        <LayoutDefault onClose={onClose || (() => {})} isSidebarOpen={isSidebarOpen} isEmbedded={isEmbedded}>
            <div className="h-screen overflow-y-auto bg-[#F8F8F8]">
                {/* Header */}
                <div className="bg-white px-6 py-4 border-b border-gray-200">
                    <h1 className="text-xl font-bold text-gray-800">Theo dõi trạng thái vận chuyển</h1>
                    <p className="text-sm text-gray-500 mt-1">Theo dõi tiến độ bàn giao, vận chuyển và trả hiện vật mượn</p>
                </div>
                {/* Search Section */}
                <div className="bg-[#ECECEC] px-4 py-2">
                    <div className="flex items-center gap-3">
                        {/* Advanced Filter Button */}
                        <button
                            ref={filterButtonRef}
                            onClick={toggleAdvancedFilter}
                            className="flex items-center gap-2 px-3 py-2 h-10 bg-white rounded-lg hover:bg-gray-50 transition-colors text-sm"
                        >
                            <div className="w-5 h-5 flex items-center justify-center">
                                <FilterIcon />
                            </div>
                            <span className="font-semibold text-black">Bộ lọc nâng cao</span>
                        </button>

                        {/* Search Box */}
                        <div className="flex-1 flex items-center bg-white border border-[#E0E0E0] rounded-lg h-10 overflow-hidden">
                            {/* Search Input Section */}
                            <div className="flex-1 flex items-center gap-2 px-3">
                                <div className="w-4 h-4 flex items-center justify-center">
                                    <SearchIcon />
                                </div>
                                <input
                                    type="text"
                                    placeholder="Tìm kiếm phiếu mượn"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                    className="flex-1 text-sm text-black placeholder-gray-400 bg-transparent outline-none"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Advanced Filter Popup */}
                    {showAdvancedFilter && (
                        <div
                            ref={filterPopupRef}
                            className="mt-6 bg-[#ECECEC] rounded-lg shadow-lg p-6 transition-all duration-300 ease-in-out"
                        >
                            <div className="flex flex-wrap gap-6">
                                {/* Status Filter */}
                                <div className="flex flex-col gap-4 w-[363px]">
                                    <label className="text-xl font-normal text-black">Trạng thái</label>
                                    <div className="relative bg-white rounded-[10px] h-[60px] flex items-center">
                                        <select
                                            value={filters.status}
                                            onChange={(e) => handleFilterChange('status', e.target.value)}
                                            className="w-full h-full px-4 text-xl text-black bg-transparent outline-none rounded-[10px]"
                                        >
                                            <option value="">Tất cả</option>
                                            <option value="approved">Đã duyệt (chờ bàn giao)</option>
                                            <option value="borrowed">Đang mượn</option>
                                            <option value="overdue">Quá hạn</option>
                                            <option value="partially_returned">Trả một phần</option>
                                            <option value="returned">Đã trả</option>
                                        </select>
                                        <div className="absolute right-4 w-8 h-8 flex items-center justify-center">
                                            <ArrowDownIcon />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Clear Customization Button */}
                            <div className="flex justify-center mt-6">
                                <button
                                    onClick={clearFilters}
                                    className="flex items-center justify-center gap-2.5 px-4 py-4 bg-[#E0E0E0] rounded-[10px] hover:bg-gray-300 transition-colors h-[60px] w-[388px]"
                                >
                                    <div className="w-10 h-10 flex items-center justify-center">
                                        <FilterIcon />
                                    </div>
                                    <span className="text-xl font-normal text-black">Xóa tùy chỉnh</span>
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Header Section */}
                <div className="bg-[#ECECEC] px-4 py-2">
                    <div className="flex items-center justify-between">
                        {/* Title */}
                        <h1 className="text-sm font-bold uppercase text-black">Theo dõi tình trạng bàn giao</h1>

                        {/* Mode Switch (if setMode is provided) */}
                        {setMode && (
                            <div className="flex items-center gap-0">
                                <span className="text-xs font-bold text-[#B0B0B0]">Dành cho người bàn giao</span>
                                <button
                                    className="w-4 h-4 flex items-center justify-center ml-1"
                                    onClick={() => setMode('partner')}
                                    title="Chuyển sang chế độ Đối tác"
                                >
                                    <SwitchModeIcon />
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Loading State */}
                {loading && (
                    <div className="flex items-center justify-center py-20">
                        <LoadingSpinnerIcon className="animate-spin h-12 w-12 text-blue-500" />
                        <span className="ml-4 text-xl text-gray-600">Đang tải dữ liệu...</span>
                    </div>
                )}

                {/* Error State */}
                {error && (
                    <div className="mx-6 mt-4 p-4 bg-red-100 border border-red-400 rounded-lg">
                        <p className="text-red-700">{error}</p>
                        <button
                            onClick={fetchDeliveryRecords}
                            className="mt-2 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                        >
                            Thử lại
                        </button>
                    </div>
                )}

                {/* Table Header */}
                {!loading && !error && (
                    <div className="overflow-x-auto w-full">
                        <div className="min-w-[1000px]">
                            <div className="bg-white shadow-sm mx-0">
                            <div className="flex items-center h-10">
                                <div className="w-2 h-10"></div>
                                <div className="flex-1 flex items-center px-4">
                                    <div className="flex-1">
                                        <span className="text-sm font-bold text-black">ID phiếu mượn</span>
                                    </div>
                                    <div className="flex-1">
                                        <span className="text-sm font-bold text-black">Tên người mượn</span>
                                    </div>
                                    <div className="flex-1">
                                        <span className="text-sm font-bold text-black">Đơn vị</span>
                                    </div>
                                    <div className="flex-1 flex items-center gap-1">
                                        <span className="text-sm font-bold text-black">Trạng thái</span>
                                    </div>
                                    <div className="w-[280px] flex gap-4">
                                        <div className="flex-1 text-center">
                                            <span className="text-sm font-bold text-black">Bàn giao</span>
                                        </div>
                                        <div className="flex-1 text-center">
                                            <span className="text-sm font-bold text-black">Nhận trả</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Scroll List */}
                        <div className="h-[539px] overflow-y-auto">
                            {deliveryRequests.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full text-gray-500">
                                    <p className="text-xl">Không có bản ghi bàn giao nào</p>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-0.5">
                                    {deliveryRequests.map((request, index) => {
                                        const actions = getActionText(request.status);
                                        return (
                                            <div key={`${request.id}-${index}`} className="bg-white flex items-center h-10">
                                                <div
                                                    className="w-2 h-10"
                                                    style={{ backgroundColor: getStatusColor(request.status) }}
                                                ></div>
                                                <div className="flex-1 flex items-center px-4 relative">
                                                    <div className="absolute inset-0 bg-white"></div>
                                                    <div className="flex-1 relative z-10">
                                                        <span className="text-sm font-normal text-black">{request.id}</span>
                                                    </div>
                                                    <div className="flex-1 relative z-10">
                                                        <span className="text-sm font-normal text-black">{request.borrowerName}</span>
                                                    </div>
                                                    <div className="flex-1 relative z-10">
                                                        <span className="text-sm font-normal text-black">{request.unit}</span>
                                                    </div>
                                                    <div className="flex-1 relative z-10">
                                                        <div
                                                            className="inline-block px-2 py-1 rounded text-sm font-normal"
                                                            style={{
                                                                backgroundColor: getStatusBgColor(request.status),
                                                            }}
                                                        >
                                                            <span style={{ color: getStatusTextColor(request.status) }}>
                                                                {getStatusText(request.status, request.overdueDays)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="w-[280px] relative z-10 flex gap-3">
                                                        {/* Delivery Action */}
                                                        <div className="flex-1">
                                                            {actions.delivery && (
                                                                <button
                                                                    onClick={() => handleDeliveryAction(request.loanId.toString(), request.status, actions.delivery)}
                                                                    className={`w-full rounded-lg px-2 py-1 h-8 flex items-center justify-center text-sm font-normal transition-colors ${getActionButtonStyle(actions.delivery)}`}
                                                                >
                                                                    <span className="truncate">{actions.delivery}</span>
                                                                </button>
                                                            )}
                                                        </div>
                                                        {/* Return Action */}
                                                        <div className="flex-1">
                                                            {actions.return && (
                                                                <button
                                                                    onClick={() => handleDeliveryAction(request.loanId.toString(), request.status, actions.return)}
                                                                    className={`w-full rounded-lg px-2 py-1 h-8 flex items-center justify-center text-sm font-normal transition-colors ${getActionButtonStyle(actions.return)}`}
                                                                >
                                                                    <span className="truncate">{actions.return}</span>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Pagination */}
                        {pagination.totalPages > 1 && (
                            <div className="flex items-center justify-center gap-4 py-4 bg-white border-t">
                                <button
                                    onClick={() => handlePageChange(pagination.page - 1)}
                                    disabled={pagination.page === 1}
                                    className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
                                >
                                    Trang trước
                                </button>
                                <span className="text-lg">
                                    Trang {pagination.page} / {pagination.totalPages}
                                </span>
                                <button
                                    onClick={() => handlePageChange(pagination.page + 1)}
                                    disabled={pagination.page === pagination.totalPages}
                                    className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
                                >
                                    Trang sau
                                </button>
                            </div>
                        )}
                        </div>
                    </div>
                )}
            </div>

            {/* Detail Modal/Page */}
            {showDeliveryDetailPage && (
                <TrackDeliveryStatusDetail
                    isOpen={showDeliveryDetailPage}
                    onClose={(shouldRefresh?: boolean) => {
                        setShowDeliveryDetailPage(false);
                        setSelectedDeliveryId('');
                        if (shouldRefresh === true) {
                            fetchDeliveryRecords();
                        }
                    }}
                    deliveryId={selectedDeliveryId}
                    deliveryStatus={selectedDeliveryStatus}
                    isModal={true}
                    isSidebarOpen={isSidebarOpen}
                />
            )}
        </LayoutDefault>
    );
}
